import { useEffect } from "preact/hooks";
import type { JSX } from "preact";
import type { Attachment } from "../api";
import { useI18n } from "../lib/i18n";
import { bytes } from "../lib/format";
import { ClipIcon } from "./Icons";

/**
 * Pictures and documents in a conversation.
 *
 * The checks here only turn an obvious mistake into a sentence before an
 * upload starts; what is accepted is decided by the server from the bytes.
 */

const ALLOWED = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);
/** What the team may send besides a picture; mirrors the server's sniffDocument. */
const DOCUMENT_NAME = /\.(pdf|txt|log)$/i;

export type ImageProblem = { kind: "type" } | { kind: "size"; mb: number };

export function precheckImage(file: { type: string; size: number }, maxBytes = 10 * 1024 * 1024): ImageProblem | null {
  if (!ALLOWED.has(file.type)) return { kind: "type" };
  if (file.size > maxBytes) return { kind: "size", mb: Math.round(maxBytes / 1024 / 1024) };
  return null;
}

/** The same check for the reply box, which also takes a PDF or a text file. */
export function precheckFile(
  file: { type: string; size: number; name: string },
  maxBytes = 10 * 1024 * 1024,
): ImageProblem | null {
  if (!ALLOWED.has(file.type) && !DOCUMENT_NAME.test(file.name)) return { kind: "type" };
  if (file.size > maxBytes) return { kind: "size", mb: Math.round(maxBytes / 1024 / 1024) };
  return null;
}

export function isImage(item: { mimeType: string }): boolean {
  return item.mimeType.startsWith("image/");
}

/** The first image in a paste or a drop, if there is one. */
export function firstImage(list: DataTransferItemList | FileList | null | undefined): File | null {
  if (!list) return null;
  for (const entry of Array.from(list as ArrayLike<DataTransferItem | File>)) {
    const file = "getAsFile" in entry ? (entry.kind === "file" ? entry.getAsFile() : null) : entry;
    if (file && file.type.startsWith("image/")) return file;
  }
  return null;
}

/** The first file of any kind in a drop; the precheck decides whether it may be sent. */
export function firstFile(list: FileList | null | undefined): File | null {
  return list && list.length > 0 ? list[0]! : null;
}

export function AttachmentList({
  items,
  onOpen,
  onLoad,
}: {
  items: Attachment[] | undefined;
  onOpen: (item: Attachment) => void;
  /** An image changes the thread's height when it arrives, after the scroll already happened. */
  onLoad?: () => void;
}): JSX.Element | null {
  const { lang } = useI18n();
  if (!items || items.length === 0) return null;
  return (
    <div class="attachments">
      {items.map((item) =>
        isImage(item) ? (
          <button key={item.id} class="thumb" onClick={() => onOpen(item)} title={item.fileName}>
            <img src={item.url} alt={item.fileName} loading="lazy" onLoad={onLoad} />
          </button>
        ) : (
          // The server answers a document as a download whatever the link
          // says, so this never opens one inside the console.
          <a key={item.id} class="file-chip" href={item.url} title={item.fileName}>
            <ClipIcon size={16} />
            <span class="file-name">{item.fileName}</span>
            <span class="hint">{bytes(item.sizeBytes, lang)}</span>
          </a>
        ),
      )}
    </div>
  );
}

export function ImageViewer({ item, onClose }: { item: Attachment; onClose: () => void }): JSX.Element {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div class="viewer" role="dialog" aria-label={item.fileName} onClick={onClose}>
      <img src={item.url} alt={item.fileName} onClick={(e) => e.stopPropagation()} />
      <div class="viewer-bar" onClick={(e) => e.stopPropagation()}>
        <span>{item.fileName}</span>
        <a class="btn sm" href={`${item.url}&download=1`}>
          {t.common.download}
        </a>
        <button class="btn sm" onClick={onClose}>
          {t.common.close}
        </button>
      </div>
    </div>
  );
}
